// src/pages/OrganizationRosterPage.jsx
// Route: /organizations/:id — ADMIN / HEALTH_NAVIGATOR only.
//
// Upload a roster (.xlsx: First Name, Last Name, Phone required; Email, Country,
// Employee ID optional) and see where each invitation stands.
//
// What an upload does: creates one PENDING invitation per new person and returns a
// personal link for each, once. What it does NOT do: create a patient, record a
// consent, or message anyone. The person opens their link, confirms the phone with
// a WhatsApp code, and accepts the consent themselves — see InvitePage. Staff
// holding a link cannot accept for them, because the code goes to the employee.

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { organizationAPI } from '../services/api';
import Icon from '../components/Icon';

const MAX_FILE_MB = 10;

const STATUS_BADGE = {
  PENDING:  'badge-pending',
  ACCEPTED: 'badge-active',
  DECLINED: 'badge-unknown',
  EXPIRED:  'badge-unknown',
  REVOKED:  'badge-unknown',
};
const STATUS_LABEL = {
  PENDING: 'Invited', ACCEPTED: 'Joined', DECLINED: 'Declined', EXPIRED: 'Expired', REVOKED: 'Revoked',
};

const isXlsx = f => !!f && /\.xlsx$/i.test(f.name || '');
const csvCell = v => `"${String(v ?? '').replace(/"/g, '""')}"`;

function downloadLinksCsv(invites, orgName) {
  const rows = [['Employee ID', 'First Name', 'Last Name', 'Invitation link']]
    .concat(invites.map(i => [i.employeeRef, i.firstName, i.lastName, i.claimUrl]));
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${orgName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-invitation-links.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function OrganizationRosterPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAllowed = ['ADMIN', 'HEALTH_NAVIGATOR'].includes(user?.role);
  const fileInputRef = useRef(null);

  const [overview, setOverview]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [copied, setCopied]       = useState(null);

  function load() {
    return organizationAPI.overview(id)
      .then(res => setOverview(res.data))
      .catch(e => setError(e.response?.data?.message || 'Could not load this organisation.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { if (isAllowed) load(); }, [id, isAllowed]);

  const crumbs = [{ label: 'Organisations', to: '/organizations' }, { label: overview?.organizationName || 'Roster' }];

  if (!isAllowed) {
    return (
      <AppShell breadcrumbs={crumbs}>
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={20} /></div>
          <div className="empty-title">Access Required</div>
          <div className="empty-desc">You need admin or health navigator privileges to manage rosters.</div>
        </div>
      </AppShell>
    );
  }

  function pick(f) {
    if (!f) return;
    if (!isXlsx(f)) { setError('Please select an .xlsx file — other formats are not supported.'); setFile(null); return; }
    if (f.size > MAX_FILE_MB * 1024 * 1024) { setError(`File too large — maximum ${MAX_FILE_MB}MB`); setFile(null); return; }
    setFile(f); setError(''); setResult(null);
  }

  async function upload() {
    if (!file) return;
    setError(''); setUploading(true); setResult(null);
    try {
      const res = await organizationAPI.importRoster(id, file);
      setResult(res.data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function copy(link) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(link);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard unavailable — the CSV download is the fallback */ }
  }

  async function revoke(inviteId) {
    if (!window.confirm('Revoke this invitation? The link will stop working.')) return;
    try {
      await organizationAPI.revoke(id, inviteId);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not revoke the invitation.');
    }
  }

  const counts = overview?.counts || {};
  const ended = (counts.EXPIRED || 0) + (counts.REVOKED || 0);

  return (
    <AppShell breadcrumbs={crumbs}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">{overview?.organizationName || 'Organisation'}</div>
          <div className="page-subtitle">
            Upload a roster to create invitations. Nobody becomes a patient until they accept the
            consent themselves.
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="stats-row fade-in-up delay-1" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-label">Invited</div><div className="stat-value">{counts.PENDING || 0}</div></div>
        <div className="stat-card"><div className="stat-label">Joined</div><div className="stat-value" style={{ color: 'var(--green)' }}>{counts.ACCEPTED || 0}</div></div>
        <div className="stat-card"><div className="stat-label">Declined</div><div className="stat-value">{counts.DECLINED || 0}</div></div>
        <div className="stat-card"><div className="stat-label">Expired / revoked</div><div className="stat-value">{ended}</div></div>
      </div>

      <div className="card fade-in-up delay-1" style={{ marginTop: 20 }}>
        <div className="card-title">Upload a roster</div>
        <div className="form-hint" style={{ marginBottom: 12 }}>
          .xlsx with a header row. Required columns: First Name, Last Name, Phone (mobile). Optional:
          Email, Country (IN or AE), Employee ID. Up to 1,000 rows.
        </div>
        <div className="toolbar">
          <button type="button" className="btn btn-secondary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            <Icon name="paperclip" size={14} /> {file ? file.name : 'Choose spreadsheet'}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }}
            onChange={e => pick(e.target.files[0])} />
          <button type="button" className="btn btn-primary" disabled={!file || uploading} onClick={upload}>
            {uploading ? 'Uploading…' : 'Create invitations'}
          </button>
        </div>
      </div>

      {result && (
        <div className="fade-in-up delay-1" style={{ marginTop: 20 }}>
          <div className={`alert ${result.created > 0 ? 'alert-success' : 'alert-info'}`}>
            {result.created} invitation{result.created === 1 ? '' : 's'} created, {result.skipped} skipped.
          </div>

          {result.invites.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-title">Invitation links</div>
              {/* One child: .alert is a flex container, so loose text beside <strong> would
                  be laid out as separate items with gaps between them. */}
              <div className="alert alert-warning">
                <span>
                  These links are shown <strong>once</strong> — only a hash is stored. Share each with its own
                  person. A link on its own does nothing: they must also enter a code sent to their own
                  WhatsApp, so no one else can accept or decline for them.
                </span>
              </div>
              <div className="page-actions" style={{ margin: '0 0 12px' }}>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => downloadLinksCsv(result.invites, overview?.organizationName || 'organisation')}>
                  Download all as CSV
                </button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Employee ID</th><th>Link</th></tr></thead>
                  <tbody>
                    {result.invites.map(i => (
                      <tr key={i.claimUrl}>
                        <td>{i.firstName} {i.lastName}</td>
                        <td>{i.employeeRef || '—'}</td>
                        <td>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(i.claimUrl)}>
                            {copied === i.claimUrl ? 'Copied ✓' : 'Copy link'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.issues.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-title">Rows not invited ({result.issues.length})</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Row</th><th>Reason</th></tr></thead>
                  <tbody>
                    {result.issues.map((i, n) => (<tr key={`${i.row}-${n}`}><td>{i.row}</td><td>{i.message}</td></tr>))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card fade-in-up delay-2" style={{ marginTop: 20 }}>
        <div className="card-title">Invitations</div>
        {loading ? (
          <div className="skeleton skeleton--row" />
        ) : !overview || overview.invites.length === 0 ? (
          <div className="empty-state empty-state--panel">
            <div className="empty-title">No invitations yet</div>
            <div className="empty-desc">Upload a roster above to create the first ones.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Phone</th><th>Employee ID</th><th>Status</th><th>Invited</th><th /></tr>
              </thead>
              <tbody>
                {overview.invites.map(i => (
                  <tr key={i.id}>
                    <td>{i.firstName ? `${i.firstName} ${i.lastName}` : '—'}</td>
                    <td>{i.maskedPhone || '—'}</td>
                    <td>{i.employeeRef || '—'}</td>
                    <td><span className={`badge ${STATUS_BADGE[i.status]}`}>{STATUS_LABEL[i.status]}</span></td>
                    <td>{new Date(i.invitedAt).toLocaleDateString()}</td>
                    <td>
                      {i.status === 'PENDING' && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => revoke(i.id)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
